// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// Leaflet state choropleth. Boundaries come from the vendored ABS GeoJSON —
// never hand-authored. Every polygon carries a hover tooltip; click drills down.
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { type BirthsData, lastVal, exNuptialShare, fmt, fmtInt } from './data';
import { tealRamp, warmRamp } from './charts';

export type MapMode = 'tfr' | 'cbr' | 'medMother' | 'births' | 'exShare';

export interface MapModeDef { id: MapMode; label: string; unit: string; dp: number; ramp: (t: number) => string; ref?: number; refLabel?: string }
export const MAP_MODES: MapModeDef[] = [
  { id: 'tfr', label: 'Fertility rate', unit: '', dp: 3, ramp: tealRamp, ref: 2.1, refLabel: 'replacement 2.1' },
  { id: 'cbr', label: 'Crude birth rate', unit: ' / 1,000', dp: 1, ramp: tealRamp },
  { id: 'medMother', label: 'Median age of mother', unit: ' yrs', dp: 1, ramp: warmRamp },
  { id: 'exShare', label: 'Born outside marriage', unit: '%', dp: 1, ramp: warmRamp },
  { id: 'births', label: 'Total births', unit: '', dp: 0, ramp: tealRamp },
];

export function metricValue(data: BirthsData, code: string, mode: MapMode): number | null {
  const s = data.summary[code];
  if (!s) return null;
  switch (mode) {
    case 'tfr': return lastVal(s.tfr);
    case 'cbr': return lastVal(s.cbr);
    case 'medMother': return lastVal(s.medMother);
    case 'births': return lastVal(s.births);
    case 'exShare': return lastVal(exNuptialShare(s));
  }
}

export interface MapController { setMode(mode: MapMode): void; destroy(): void }

export async function renderMap(container: HTMLElement, data: BirthsData, initial: MapMode, onSelect: (code: string) => void): Promise<MapController> {
  container.innerHTML = '<div class="map-canvas"></div>';
  const canvas = container.querySelector('.map-canvas') as HTMLElement;

  const map = L.map(canvas, { minZoom: 3, maxZoom: 8, zoomControl: true, scrollWheelZoom: false, attributionControl: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Boundaries: ABS ASGS 2021 (CC BY 4.0)', subdomains: 'abcd', minZoom: 3, maxZoom: 8,
  }).addTo(map);

  const geo = await fetch('data/boundaries.geojson').then((r) => { if (!r.ok) throw new Error('boundaries'); return r.json(); });

  let mode: MapMode = initial;
  const modeDef = () => MAP_MODES.find((m) => m.id === mode)!;
  const domain = (): [number, number] => {
    const vals = data.states.map((c) => metricValue(data, c, mode)).filter((v): v is number => v != null);
    return vals.length ? [Math.min(...vals), Math.max(...vals)] : [0, 1];
  };
  const colour = (code: string): string => {
    const v = metricValue(data, code, mode);
    if (v == null) return '#e5e7eb';
    const [lo, hi] = domain();
    const t = hi === lo ? 0.5 : (v - lo) / (hi - lo);
    return modeDef().ramp(0.15 + t * 0.85);
  };
  const tipHtml = (code: string): string => {
    const name = data.regionNames[code] ?? code;
    const md = modeDef();
    const v = metricValue(data, code, mode);
    const shown = v == null ? '—' : (mode === 'births' ? fmtInt(v) : fmt(v, md.dp)) + md.unit;
    return `<strong>${name}</strong><br>${md.label}: ${shown}`;
  };

  const layer = L.geoJSON(geo, {
    style: (f: any) => ({ fillColor: colour(f.properties.code), fillOpacity: 0.85, color: '#ffffff', weight: 1 }),
    onEachFeature: (f: any, lyr: any) => {
      lyr.bindTooltip(tipHtml(f.properties.code), { sticky: true, className: 'map-tip', direction: 'top' });
      lyr.on({
        mouseover: () => lyr.setStyle({ weight: 2.4, color: '#0f172a' }),
        mouseout: () => layer.resetStyle(lyr),
        click: () => onSelect(f.properties.code),
      });
    },
  }).addTo(map);

  // Legend control.
  const legend = new L.Control({ position: 'bottomright' });
  legend.onAdd = () => {
    const div = L.DomUtil.create('div', 'map-legend');
    renderLegend(div);
    return div;
  };
  legend.addTo(map);
  function renderLegend(div: HTMLElement) {
    const md = modeDef(); const [lo, hi] = domain();
    let swatches = '';
    for (let i = 0; i <= 4; i++) {
      const t = i / 4;
      const val = lo + t * (hi - lo);
      swatches += `<span class="lg-swatch" style="background:${md.ramp(0.15 + t * 0.85)}"></span><span class="lg-tick">${mode === 'births' ? fmtInt(val) : fmt(val, md.dp)}</span>`;
    }
    div.innerHTML = `<div class="lg-title">${md.label}${md.unit ? ` (${md.unit.trim()})` : ''}</div><div class="lg-scale">${swatches}</div>`;
  }

  // Zero-size / cold-load defence. A single fit can run before Leaflet has
  // settled its projection (a cold deep-link to the map view showed a
  // world-wrapped map), so re-fit at several delays and on every resize until
  // the view actually matches the boundary bounds, then stop.
  const bounds = layer.getBounds();
  let settled = false;
  const fit = () => {
    if (!bounds.isValid() || canvas.clientHeight <= 50) return;
    map.invalidateSize();
    map.fitBounds(bounds, { padding: [10, 10] });
  };
  const timers = [60, 200, 500, 1000, 1800].map((t) => setTimeout(fit, t));
  map.whenReady(fit);
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) fit(); });
  ro.observe(canvas);
  // Stop retrying once the projected bounds sit inside the viewport.
  const stopper = setInterval(() => {
    if (settled) return;
    if (bounds.isValid() && map.getBounds().contains(bounds)) { settled = true; clearInterval(stopper); }
  }, 300);
  setTimeout(() => { settled = true; clearInterval(stopper); }, 4000);

  return {
    setMode(m: MapMode) {
      mode = m;
      layer.setStyle((f: any) => ({ fillColor: colour(f.properties.code), fillOpacity: 0.85, color: '#ffffff', weight: 1 }));
      layer.eachLayer((lyr: any) => lyr.setTooltipContent(tipHtml(lyr.feature.properties.code)));
      const el = container.querySelector('.map-legend') as HTMLElement | null;
      if (el) renderLegend(el);
    },
    destroy() { timers.forEach(clearTimeout); clearInterval(stopper); ro.disconnect(); map.remove(); },
  };
}
